import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiX, FiSettings } = FiIcons;

export const GDPRPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('gdpr-consent');
    if (!consent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('gdpr-consent', JSON.stringify({
      accepted: true,
      date: new Date().toISOString(),
      preferences: {
        necessary: true,
        analytics: true,
        marketing: true
      }
    }));
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('gdpr-consent', JSON.stringify({
      accepted: false,
      date: new Date().toISOString(),
      preferences: {
        necessary: true,
        analytics: false,
        marketing: false
      }
    }));
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:max-w-md z-50"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Privacy & Cookies</h3>
              </div>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            {!showDetails ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  We use cookies and similar technologies to enhance your experience, 
                  analyze performance, and personalize content. By continuing to use our 
                  service, you consent to our use of cookies.
                </p>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleAccept}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleCustomize}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 hover:border-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    <SafeIcon icon={FiSettings} className="w-4 h-4 mr-1" />
                    Customize
                  </button>
                </div>
              </>
            ) : (
              <CookiePreferences 
                onSave={(preferences) => {
                  localStorage.setItem('gdpr-consent', JSON.stringify({
                    accepted: true,
                    date: new Date().toISOString(),
                    preferences
                  }));
                  setIsVisible(false);
                }}
                onBack={() => setShowDetails(false)}
              />
            )}

            <div className="mt-3 text-xs text-gray-500">
              <a href="/privacy" className="hover:text-gray-700 underline">Privacy Policy</a>
              {' • '}
              <a href="/cookies" className="hover:text-gray-700 underline">Cookie Policy</a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CookiePreferences = ({ onSave, onBack }) => {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: true,
    marketing: true
  });

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      name: 'Necessary Cookies',
      description: 'Required for basic site functionality and security.',
      required: true
    },
    {
      key: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how you use our site to improve performance.'
    },
    {
      key: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to personalize ads and measure advertising effectiveness.'
    }
  ];

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Cookie Preferences</h4>
      
      <div className="space-y-3">
        {cookieTypes.map((type) => (
          <div key={type.key} className="flex items-start justify-between">
            <div className="flex-1">
              <h5 className="text-sm font-medium text-gray-900">{type.name}</h5>
              <p className="text-xs text-gray-500 mt-1">{type.description}</p>
            </div>
            <button
              onClick={() => togglePreference(type.key)}
              disabled={type.required}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ml-3 ${
                preferences[type.key] ? 'bg-blue-600' : 'bg-gray-200'
              } ${type.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  preferences[type.key] ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-2 border border-gray-300 hover:border-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => onSave(preferences)}
          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Save Preferences
        </button>
      </div>
    </div>
  );
};